import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AlertCircleIcon, CreditCardIcon, LoaderIcon, ClockIcon, SparklesIcon } from 'lucide-react';
import { openPaddleCheckout, openPaddleUpdatePayment } from '../services/paddleService';
type BlockReason = 'trial_expired' | 'past_due' | 'canceled' | 'no_subscription';
export const SubscriptionGuard: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const {
    currentUser,
    userData,
    userType,
    loading: authLoading
  } = useAuth();
  const navigate = useNavigate();
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockReason, setBlockReason] = useState<BlockReason | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState<number | null>(null);
  // Helper: safely convert Firestore Timestamp or ISO string to JS Date
  const parseFirestoreDate = (value: any): Date | null => {
    if (!value) return null;
    // Firestore Timestamp object (has .toDate() method)
    if (typeof value?.toDate === 'function') {
      return value.toDate();
    }
    // Firestore Timestamp-like object (has .seconds property)
    if (typeof value?.seconds === 'number') {
      return new Date(value.seconds * 1000);
    }
    // ISO string or other string
    if (typeof value === 'string') {
      const parsed = new Date(value);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    // Number (epoch ms)
    if (typeof value === 'number') {
      return new Date(value);
    }
    console.error('[SubscriptionGuard] Could not parse date value:', value, 'type:', typeof value);
    return null;
  };
  useEffect(() => {
    const checkSubscription = () => {
      setIsLoading(true);
      // DEBUG: Detailed logging with types
      const sub = userData?.subscription;
      console.log('[SubscriptionGuard] === CHECK START ===');
      console.log('[SubscriptionGuard] userType:', userType);
      console.log('[SubscriptionGuard] userData exists:', !!userData);
      console.log('[SubscriptionGuard] subscription exists:', !!sub);
      console.log('[SubscriptionGuard] subscription.status:', sub?.status, '(type:', typeof sub?.status, ')');
      console.log('[SubscriptionGuard] subscription.trialEndsAt:', sub?.trialEndsAt, '(type:', typeof sub?.trialEndsAt, ')');
      if (sub?.trialEndsAt && typeof sub.trialEndsAt === 'object') {
        console.log('[SubscriptionGuard] trialEndsAt keys:', Object.keys(sub.trialEndsAt));
        console.log('[SubscriptionGuard] trialEndsAt.seconds:', (sub.trialEndsAt as any)?.seconds);
        console.log('[SubscriptionGuard] trialEndsAt.toDate:', typeof (sub.trialEndsAt as any)?.toDate);
      }
      // If auth is still loading, don't make any decisions yet
      if (authLoading) {
        console.log('[SubscriptionGuard] Auth still loading — waiting');
        setIsLoading(true);
        return;
      }
      // If userData hasn't arrived yet for a teacher, wait — don't block
      if (userType === 'teacher' && !userData) {
        console.log('[SubscriptionGuard] Teacher but userData is null — waiting (not blocking)');
        setIsLoading(true);
        return;
      }
      // Students don't need subscription checks
      if (userType === 'student') {
        console.log('[SubscriptionGuard] Student user — bypassing');
        setIsBlocked(false);
        setIsLoading(false);
        return;
      }
      if (!sub) {
        console.log('[SubscriptionGuard] No subscription object — blocking');
        setIsBlocked(true);
        setBlockReason('no_subscription');
        setIsLoading(false);
        return;
      }
      const {
        status,
        trialEndsAt
      } = sub;
      if (status === 'active') {
        console.log('[SubscriptionGuard] Status: active — allowing');
        setIsBlocked(false);
        setTrialDaysRemaining(null);
        setIsLoading(false);
        return;
      }
      if (status === 'trial') {
        const trialEnd = parseFirestoreDate(trialEndsAt);
        console.log('[SubscriptionGuard] Status: trial, parsed trialEnd:', trialEnd);
        if (trialEnd) {
          const now = new Date();
          console.log('[SubscriptionGuard] now:', now.toISOString(), 'trialEnd:', trialEnd.toISOString());
          if (now >= trialEnd) {
            console.log('[SubscriptionGuard] Trial EXPIRED — blocking');
            setIsBlocked(true);
            setBlockReason('trial_expired');
            setTrialDaysRemaining(0);
          } else {
            const msRemaining = trialEnd.getTime() - now.getTime();
            const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));
            console.log('[SubscriptionGuard] Trial ACTIVE — allowing, days remaining:', daysRemaining);
            setTrialDaysRemaining(daysRemaining);
            setIsBlocked(false);
          }
        } else {
          // Trial status but unparseable/missing end date — allow access (defensive)
          console.log('[SubscriptionGuard] Trial status but no parseable trialEndsAt — allowing (defensive)');
          setIsBlocked(false);
        }
        setIsLoading(false);
        return;
      }
      if (status === 'past_due') {
        console.log('[SubscriptionGuard] Status: past_due — blocking');
        setIsBlocked(true);
        setBlockReason('past_due');
        setIsLoading(false);
        return;
      }
      if (status === 'canceled') {
        console.log('[SubscriptionGuard] Status: canceled — blocking');
        setIsBlocked(true);
        setBlockReason('canceled');
        setIsLoading(false);
        return;
      }
      // Unknown status — block
      console.log('[SubscriptionGuard] Unknown status:', status, '— blocking');
      setIsBlocked(true);
      setBlockReason('no_subscription');
      setIsLoading(false);
    };
    checkSubscription();
  }, [userData, userType, authLoading]);
  const handleSubscribe = async () => {
    if (!currentUser) return;
    try {
      setIsProcessing(true);
      await openPaddleCheckout(currentUser.email || '', currentUser.uid, currentUser.displayName || '');
    } catch (error) {
      console.error('[SubscriptionGuard] Error opening checkout:', error);
    } finally {
      setIsProcessing(false);
    }
  };
  const handleUpdatePayment = async () => {
    if (userData?.subscription?.paddleSubscriptionId) {
      try {
        setIsProcessing(true);
        await openPaddleUpdatePayment(userData.subscription.paddleSubscriptionId);
      } catch (error) {
        console.error('[SubscriptionGuard] Error opening payment update:', error);
      } finally {
        setIsProcessing(false);
      }
    }
  };
  // Loading state
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50" data-id="element-11710">
        <LoaderIcon className="h-8 w-8 animate-spin text-deep-pink" data-id="element-11711" />
      </div>;
  }
  // Allow access — optionally show trial banner
  if (!isBlocked) {
    return <>
        {trialDaysRemaining !== null && trialDaysRemaining <= 7 && <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-center" data-id="element-11712">
            <div className="flex items-center justify-center gap-2 text-sm text-amber-800" data-id="element-11713">
              <ClockIcon className="h-4 w-4 flex-shrink-0" data-id="element-11714" />
              <span data-id="element-11715">
                {trialDaysRemaining === 0 ? 'Your free trial ends today.' : trialDaysRemaining === 1 ? 'Your free trial ends tomorrow.' : `Your free trial ends in ${trialDaysRemaining} days.`}
              </span>
              <button onClick={handleSubscribe} className="ml-1 font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950 transition-colors" data-id="element-11716">
                Subscribe now
              </button>
            </div>
          </div>}
        {children}
      </>;
  }
  // ── Paywall: Trial Expired ──
  if (blockReason === 'trial_expired') {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-id="element-11717">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full" data-id="element-11718">
          <div className="text-center" data-id="element-11719">
            <div className="bg-amber-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center" data-id="element-11720">
              <SparklesIcon className="h-10 w-10 text-amber-600" data-id="element-11721" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-11722">
              Your Free Trial Has Ended
            </h2>
            <p className="text-gray-600 mb-6" data-id="element-11723">
              We hope you enjoyed exploring MaestroConnect! Subscribe to keep
              managing your students, lessons, and studio — all in one place.
            </p>

            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left" data-id="element-11724">
              <p className="text-sm font-semibold text-gray-900 mb-2" data-id="element-11725">
                What you get with your subscription:
              </p>
              <ul className="text-sm text-gray-600 space-y-1.5" data-id="element-11726">
                <li className="flex items-start gap-2" data-id="element-11727">
                  <span className="text-green-500 mt-0.5" data-id="element-11728">✓</span>
                  Unlimited students & lesson scheduling
                </li>
                <li className="flex items-start gap-2" data-id="element-11729">
                  <span className="text-green-500 mt-0.5" data-id="element-11730">✓</span>
                  Task management & progress tracking
                </li>
                <li className="flex items-start gap-2" data-id="element-11731">
                  <span className="text-green-500 mt-0.5" data-id="element-11732">✓</span>
                  Reports, alerts & calendar views
                </li>
                <li className="flex items-start gap-2" data-id="element-11733">
                  <span className="text-green-500 mt-0.5" data-id="element-11734">✓</span>
                  Revenue tracking & invoicing tools
                </li>
              </ul>
            </div>

            <div className="space-y-3" data-id="element-11735">
              <button onClick={handleSubscribe} disabled={isProcessing} className="w-full bg-deep-pink hover:bg-burgundy text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-11736">
                {isProcessing ? <>
                    <LoaderIcon className="h-5 w-5 mr-2 animate-spin" data-id="element-11737" />
                    Opening checkout...
                  </> : <>
                    <CreditCardIcon className="h-5 w-5 mr-2" data-id="element-11738" />
                    Subscribe — $9.99/month
                  </>}
              </button>
              <button onClick={() => navigate('/billing-payouts')} className="w-full border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium py-3 px-6 rounded-lg transition-colors" data-id="element-11739">
                View Billing Details
              </button>
            </div>

            <p className="text-xs text-gray-400 mt-4" data-id="element-11740">
              Cancel anytime. Secure payment via Paddle.
            </p>
          </div>
        </div>
      </div>;
  }
  // ── Paywall: Past Due / Canceled / No Subscription ──
  return <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4" data-id="element-11741">
      <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full" data-id="element-11742">
        <div className="text-center" data-id="element-11743">
          <div className="bg-red-100 rounded-full p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center" data-id="element-11744">
            <AlertCircleIcon className="h-12 w-12 text-red-500" data-id="element-11745" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-11746">
            Subscription Required
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-11747">
            {blockReason === 'past_due' ? 'Your payment method needs to be updated to continue using MaestroConnect.' : blockReason === 'canceled' ? 'Your subscription has been canceled. Please resubscribe to continue.' : 'An active subscription is required to access MaestroConnect.'}
          </p>
          <div className="space-y-3" data-id="element-11748">
            {blockReason === 'past_due' && userData?.subscription?.paddleSubscriptionId && <button onClick={handleUpdatePayment} disabled={isProcessing} className="w-full bg-deep-pink hover:bg-burgundy text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-11749">
                  {isProcessing ? <>
                      <LoaderIcon className="h-5 w-5 mr-2 animate-spin" data-id="element-11750" />
                      Opening...
                    </> : <>
                      <CreditCardIcon className="h-5 w-5 mr-2" data-id="element-11751" />
                      Update Payment Method
                    </>}
                </button>}
            {(blockReason === 'canceled' || blockReason === 'no_subscription') && <button onClick={handleSubscribe} disabled={isProcessing} className="w-full bg-deep-pink hover:bg-burgundy text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-11752">
                {isProcessing ? <>
                    <LoaderIcon className="h-5 w-5 mr-2 animate-spin" data-id="element-11753" />
                    Opening checkout...
                  </> : <>
                    <CreditCardIcon className="h-5 w-5 mr-2" data-id="element-11754" />
                    Subscribe — $9.99/month
                  </>}
              </button>}
            <button onClick={() => navigate('/billing-payouts')} className="w-full border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium py-3 px-6 rounded-lg transition-colors" data-id="element-11755">
              View Billing Details
            </button>
          </div>
        </div>
      </div>
    </div>;
};